import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  type DocumentReference,
  type Query,
} from "firebase/firestore";
import { useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import {
  feedbackFormSchema,
  responseSchema,
  submissionSchema,
  type Answers,
  type FeedbackForm,
  type FeedbackResponse,
  type Submission,
} from "./schemas";

export type ListState<T> =
  | { status: "loading" }
  | { status: "error"; error: string; code?: string }
  | { status: "success"; data: T[] };

export type DocState<T> =
  | { status: "loading" }
  | { status: "not-found" }
  | { status: "error"; error: string; code?: string }
  | { status: "success"; data: T };

function errorOf(error: unknown): { error: string; code?: string } {
  const code =
    typeof error === "object" && error !== null && "code" in error
      ? String((error as { code: unknown }).code)
      : undefined;
  return {
    error: error instanceof Error ? error.message : "Failed to load",
    code,
  };
}

function useLiveList<T>(
  buildQuery: (() => Query) | null,
  parse: (id: string, data: unknown) => T | null,
  key: string,
): ListState<T> {
  const [state, setState] = useState<ListState<T>>({ status: "loading" });
  useEffect(() => {
    if (!buildQuery) return;
    setState({ status: "loading" });
    return onSnapshot(
      buildQuery(),
      (snapshot) => {
        const data: T[] = [];
        snapshot.forEach((item) => {
          const parsed = parse(item.id, item.data());
          if (parsed) data.push(parsed);
        });
        setState({ status: "success", data });
      },
      (error) => setState({ status: "error", ...errorOf(error) }),
    );
    // `key` names the query; the builders are recreated every render.
  }, [key]);
  return state;
}

function useLiveDoc<T>(
  ref: DocumentReference | null,
  parse: (id: string, data: unknown) => T | null,
  key: string,
): DocState<T> {
  const [state, setState] = useState<DocState<T>>({ status: "loading" });
  useEffect(() => {
    if (!ref) return;
    setState({ status: "loading" });
    return onSnapshot(
      ref,
      (snapshot) => {
        if (!snapshot.exists()) {
          setState({ status: "not-found" });
          return;
        }
        const parsed = parse(snapshot.id, snapshot.data());
        setState(
          parsed
            ? { status: "success", data: parsed }
            : { status: "error", error: "This document could not be read." },
        );
      },
      (error) => setState({ status: "error", ...errorOf(error) }),
    );
  }, [key]);
  return state;
}

function parseForm(id: string, data: unknown): FeedbackForm | null {
  const result = feedbackFormSchema.safeParse({
    ...(data as object),
    id,
  });
  return result.success ? result.data : null;
}

function parseResponse(id: string, data: unknown): FeedbackResponse | null {
  const result = responseSchema.safeParse({ ...(data as object), id });
  return result.success ? result.data : null;
}

function parseSubmission(_id: string, data: unknown): Submission | null {
  const result = submissionSchema.safeParse(data);
  return result.success ? result.data : null;
}

const formsCollection = (eventId: string) =>
  collection(db, "events", eventId, "feedback_forms");

/** Every form of the event, live. Event admins only. */
export function useAdminFeedbackForms(eventId: string) {
  return useLiveList(
    () => query(formsCollection(eventId)),
    parseForm,
    `admin-forms/${eventId}`,
  );
}

/**
 * The open forms a member may read. The status filter is not an
 * optimisation: the rules admit a member to open forms only, and a query they
 * could not prove matches only those is refused whole.
 */
export function useOpenFeedbackForms(eventId: string) {
  return useLiveList(
    () => query(formsCollection(eventId), where("status", "==", "open")),
    parseForm,
    `open-forms/${eventId}`,
  );
}

export function useFeedbackForm(eventId: string, formId: string) {
  return useLiveDoc(
    doc(db, "events", eventId, "feedback_forms", formId),
    parseForm,
    `form/${eventId}/${formId}`,
  );
}

export function submissionRef(eventId: string, formId: string, uid: string) {
  return doc(
    db,
    "events",
    eventId,
    "feedback_forms",
    formId,
    "feedback_submissions",
    uid,
  );
}

/** The caller's own submission for one form, live. */
export function useOwnSubmission(eventId: string, formId: string, uid: string) {
  return useLiveDoc(
    submissionRef(eventId, formId, uid),
    parseSubmission,
    `submission/${eventId}/${formId}/${uid}`,
  );
}

/**
 * A form's responses, live. `enabled` is false for an anonymous form below the
 * results threshold, where the rules refuse the list and a query would only
 * produce a permission error.
 */
export function useFeedbackResponses(
  eventId: string,
  formId: string,
  enabled: boolean,
) {
  return useLiveList(
    enabled
      ? () =>
          query(
            collection(
              db,
              "events",
              eventId,
              "feedback_forms",
              formId,
              "feedback_responses",
            ),
          )
      : null,
    parseResponse,
    `responses/${eventId}/${formId}/${enabled}`,
  );
}

/**
 * File the member's answers. A full replace, never a merge: the rules admit
 * exactly these keys, and a merge would carry the previous outcome's `error`
 * and `processedAt` along. `anonymous` is the flag the attendee was shown; the
 * rules and the trigger both refuse it once the organiser has changed it.
 */
export function submitFeedback({
  eventId,
  formId,
  uid,
  answers,
  anonymous,
}: {
  eventId: string;
  formId: string;
  uid: string;
  answers: Answers;
  anonymous: boolean;
}): Promise<void> {
  return setDoc(submissionRef(eventId, formId, uid), {
    uid,
    answers,
    anonymous,
    status: "REQUESTED",
    submittedAt: serverTimestamp(),
  });
}
